import React, { useEffect, useState } from "react";
import { Loader2, Save, Languages } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

const PAGES = [
  { key: "hero",         label: "Anasayfa Hero" },
  { key: "about",        label: "Hakkımızda" },
  { key: "contact",      label: "İletişim" },
  { key: "postun-hazir", label: "Postun Hazır" },
];

const LANGS = ["tr", "en", "es", "de", "fr", "ar"];

/**
 * Admin Site Content editor — manages per-page content overrides
 * keyed by language. Allows admin to add free-form key/value pairs
 * (e.g. hero_title, hero_subtitle, story_p1) per language and
 * AI-translate the TR version into the other 5 languages with one click.
 */
export default function AdminSiteContentPage() {
  const [activePage, setActivePage] = useState(PAGES[0].key);
  const [activeLang, setActiveLang] = useState("tr");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [newKey, setNewKey] = useState("");

  const load = async (page) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/site-content/${page}`);
      setDoc({ page, sections: data.sections || {} });
    } catch (e) { toast.error("Yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(activePage); /* eslint-disable-next-line */ }, [activePage]);

  if (loading || !doc) {
    return <div className="p-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>;
  }

  const sections = doc.sections || {};
  const langContent = sections[activeLang] || {};

  const setField = (k, v) => {
    setDoc((d) => ({
      ...d,
      sections: {
        ...d.sections,
        [activeLang]: { ...(d.sections[activeLang] || {}), [k]: v },
      },
    }));
  };

  const removeField = (k) => {
    if (!window.confirm(`"${k}" alanını ${activeLang} dilinden silmek istediğine emin misin?`)) return;
    setDoc((d) => {
      const next = { ...(d.sections[activeLang] || {}) };
      delete next[k];
      return { ...d, sections: { ...d.sections, [activeLang]: next } };
    });
  };

  const addKey = () => {
    const k = newKey.trim();
    if (!k) return;
    if (langContent[k] !== undefined) { toast.error("Bu anahtar zaten var"); return; }
    setField(k, "");
    setNewKey("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-content/${activePage}`, { sections: doc.sections });
      toast.success("Kaydedildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  const autoTranslate = async () => {
    if (!Object.keys(sections.tr || {}).length) {
      toast.error("Önce Türkçe içerik girip kaydet, sonra çevirebilirsin.");
      return;
    }
    if (!window.confirm("TR içeriğini AI ile 5 dile çevirip mevcut çevirilerin üzerine yazmak istediğine emin misin?")) return;
    setTranslating(true);
    try {
      // Save current state first (TR could have unsaved changes)
      await api.put(`/admin/site-content/${activePage}`, { sections: doc.sections });
      const { data } = await api.post(`/admin/site-content/${activePage}/auto-translate`);
      setDoc({ page: activePage, sections: data.sections || {} });
      toast.success("AI çeviri tamamlandı");
    } catch (e) { toast.error(e?.response?.data?.detail || "Çeviri başarısız"); }
    finally { setTranslating(false); }
  };

  return (
    <div data-testid="site-content-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold">Sayfa İçerikleri</h1>
          <p className="mt-1.5 text-sm text-ink/55">
            Hero, Hakkımızda, İletişim ve Postun Hazır sayfalarındaki başlıklar/metinler — dile göre düzenlenebilir.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={autoTranslate} disabled={translating}
            className="btn-outline-ink !text-xs !py-2.5 inline-flex items-center gap-2"
            data-testid="site-content-translate-btn">
            {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
            TR → Tüm Diller (AI)
          </button>
          <button type="button" onClick={save} disabled={saving}
            data-testid="site-content-save-btn"
            className="btn-gold !text-xs !py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </button>
        </div>
      </div>

      {/* Page tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {PAGES.map((p) => (
          <button
            type="button"
            key={p.key}
            onClick={() => setActivePage(p.key)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors
              ${p.key === activePage ? "bg-brand-pink text-cream" : "bg-mist text-ink/70 hover:bg-mist/70"}`}
            data-testid={`site-content-page-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Lang tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-mist pb-3">
        {LANGS.map((l) => (
          <button
            type="button"
            key={l}
            onClick={() => setActiveLang(l)}
            className={`rounded-md px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold transition-colors
              ${l === activeLang ? "bg-ink text-cream" : "text-ink/55 hover:text-ink"}`}
            data-testid={`site-content-lang-${l}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-mist p-6 sm:p-8 max-w-3xl">
        {Object.keys(langContent).length === 0 ? (
          <p className="text-sm text-ink/55">Bu sayfa için <strong>{activeLang.toUpperCase()}</strong> dilinde içerik yok. Aşağıdan anahtar ekleyerek başla.</p>
        ) : (
          <div className="space-y-5">
            {Object.entries(langContent).map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="label-base !mb-0">{k}</label>
                  <button type="button" onClick={() => removeField(k)}
                    className="text-xs text-destructive hover:text-destructive/80"
                    data-testid={`site-content-remove-${k}`}>
                    Sil
                  </button>
                </div>
                <textarea rows={2} value={String(v ?? "")} onChange={(e) => setField(k, e.target.value)}
                  className="input-base resize-y"
                  data-testid={`site-content-field-${k}`} />
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-mist mt-7 pt-6">
          <label className="label-base">Yeni Anahtar Ekle</label>
          <div className="flex items-center gap-2">
            <input type="text" value={newKey} onChange={(e) => setNewKey(e.target.value)}
              placeholder="ör: hero_title, story_p1, cta_button"
              className="input-base flex-1"
              data-testid="site-content-new-key" />
            <button type="button" onClick={addKey}
              className="btn-outline-ink !text-xs !py-2.5"
              data-testid="site-content-add-key">Ekle</button>
          </div>
          <p className="mt-1 text-xs text-ink/45">Aktif dilde ({activeLang}) yeni metin alanı oluşturur. Kaydet sonra public sayfada görünür.</p>
        </div>
      </div>
    </div>
  );
}
